// Single source of truth for credit pack pricing and credit counts.
// This module must stay free of server-only imports (db, Polar SDK, secrets)
// so it can be safely imported from both server code (@/lib/payments) and
// client components (@/lib/payments/client) without duplicating numbers that
// can silently drift out of sync (e.g. a repricing updating one copy but not
// the other).

export type CreditPackId = "trial" | "mini" | "basic" | "starter" | "pro" | "power";

interface CreditPackBaseConfig {
  id: CreditPackId;
  name: string;
  /** Price in cents. */
  price: number;
  credits: number;
  description: string;
  popular?: boolean;
  /** Starter-only promotional credit bonus, applied when the promo is enabled. */
  promotionalCredits?: number;
}

export const CREDIT_PACK_BASE_CONFIG: Record<CreditPackId, CreditPackBaseConfig> = {
  trial: {
    id: "trial",
    name: "Trial Pack",
    price: 149, // $1.49
    credits: 1,
    description: "One-time try - perfect for testing",
  },
  mini: {
    id: "mini",
    name: "Mini Pack",
    price: 199, // $1.99
    credits: 3,
    description: "A small top-up when you need a couple more previews",
  },
  basic: {
    id: "basic",
    name: "Basic",
    price: 499, // $4.99
    credits: 30,
    description: "A flexible starting pack for new builds",
  },
  starter: {
    id: "starter",
    name: "Starter Pack",
    price: 999, // $9.99
    credits: 75,
    description: "The best fit for enthusiasts and repeat comparisons",
  },
  pro: {
    id: "pro",
    name: "Pro Pack",
    price: 1999, // $19.99
    credits: 175,
    description: "For larger builds and repeat users",
    popular: true,
  },
  power: {
    id: "power",
    name: "Power Pack",
    price: 3999, // $39.99
    credits: 400,
    description: "High-volume previews for dealers and power users",
  },
};

export function isStarterPromotionEnabled(): boolean {
  return (
    process.env.NEXT_PUBLIC_STARTER_PROMO_ENABLED === "true" ||
    process.env.STARTER_PROMO_ENABLED === "true"
  );
}

export function isPackPromotionEnabled(packId: CreditPackId): boolean {
  return packId === "starter" && isStarterPromotionEnabled();
}

export function getPackEffectiveCredits(packId: CreditPackId): number {
  const pack = CREDIT_PACK_BASE_CONFIG[packId];
  if (isPackPromotionEnabled(packId) && pack.promotionalCredits) {
    return pack.promotionalCredits;
  }
  return pack.credits;
}

/** Base (non-promotional) credit count, for copy like "X credits instead of Y". */
export function getPackBaseCredits(packId: CreditPackId): number {
  return CREDIT_PACK_BASE_CONFIG[packId].credits;
}

export function getPackName(packId: CreditPackId): string {
  return CREDIT_PACK_BASE_CONFIG[packId].name;
}

export function getPackPriceCents(packId: CreditPackId): number {
  return CREDIT_PACK_BASE_CONFIG[packId].price;
}

export function getPackPriceUsd(packId: CreditPackId): number {
  return getPackPriceCents(packId) / 100;
}
